import { Board, CellState, PlayData, cellValueBits, withValueBits } from '@puzzlio/core';

// ---------------------------------------------------------------------------
// Mosaic is a nonogram (a.k.a. picross / griddler). The player fills cells on a
// grid according to the row/column clues; the filled cells form a silhouette.
// On completion the silhouette is recoloured to reveal the original pixel-art
// image the puzzle was derived from.
//
// Gameplay only ever cares whether a cell is filled or not — the colours are
// purely a completion reward. So the player works with three cell values:
//   - EMPTY:   untouched / cleared
//   - FILLED:  the player believes this cell is part of the picture
//   - CROSSED: the player has marked this cell as definitely blank (the "X")
// ---------------------------------------------------------------------------

// Cell value constants
export const EMPTY_CELL = '.' as const;
export const FILLED_CELL = '#' as const;
export const CROSSED_CELL = 'x' as const;

export type EmptyCell = typeof EMPTY_CELL;
export type FilledCell = typeof FILLED_CELL;
export type CrossedCell = typeof CROSSED_CELL;

// Play cell value (the logical value bits of a CellState).
export type CellValue = EmptyCell | FilledCell | CrossedCell;

// ---------------------------------------------------------------------------
// CellValue <-> CellState bit packing
// Bits 0–1 hold the Mosaic CellValue; bits 2–7 hold the shared highlight state
// (colour index + edge connectivity), exactly as in the other games.
// ---------------------------------------------------------------------------

const BITS_TO_VALUE: CellValue[] = [EMPTY_CELL, FILLED_CELL, CROSSED_CELL];
const VALUE_TO_BITS: Record<CellValue, number> = {
  [EMPTY_CELL]: 0,
  [FILLED_CELL]: 1,
  [CROSSED_CELL]: 2,
};

/** Extract the Mosaic CellValue (bits 0–1) from a CellState. */
export const cellValue = (cell: CellState): CellValue => BITS_TO_VALUE[cellValueBits(cell)];

/**
 * Build a fresh CellState with the given value and colour index (0 = no highlight).
 * Edge bits are always 0 for a newly created cell.
 */
export const makeState = (value: CellValue, colorIndex: number = 0): CellState =>
  VALUE_TO_BITS[value] | ((colorIndex & 0x03) << 2);

/** Replace only the CellValue bits (0–1) while preserving all other bits (colour + edges). */
export const setCellValue = (state: CellState, value: CellValue): CellState =>
  withValueBits(state, VALUE_TO_BITS[value]);

/** Convenience: is this cell filled (part of the intended picture)? */
export const isFilled = (cell: CellState): boolean => cellValueBits(cell) === VALUE_TO_BITS[FILLED_CELL];

// ---------------------------------------------------------------------------
// Clues
// ---------------------------------------------------------------------------

/**
 * The run-lengths of consecutive filled cells in a single row or column, in
 * order. A completely empty line has an empty clue (`[]`); renderers conventionally
 * display that as a single `0`.
 */
export type Clue = number[];

/** Which axis a clue belongs to — `rows[3]` is the clue beside row 3, `cols[3]` the one above column 3. */
export type ClueAxis = 'rows' | 'cols';

/**
 * A marked cell whose `col` is CLUE_GUTTER addresses row `row`'s clue rather than a cell; likewise
 * one whose `row` is CLUE_GUTTER addresses column `col`'s clue. Hints and the tutorial use it to
 * point at the clue they're reasoning from — the clues are where a nonogram's thinking happens, so
 * they need to be markable the same way cells are.
 */
export const CLUE_GUTTER = -1;

/**
 * The clue numbers the player has ticked off as done, stored as indices into each line's clue —
 * `rows[2] = [0]` means "the first number of row 2's clue is marked". Sparse by design: lines with
 * nothing marked are absent, so an untouched board costs almost nothing to persist.
 *
 * Marking is purely the player's own bookkeeping. Nothing derives it from the solution and nothing
 * validates it, so a player is free to mark a clue they've got wrong.
 */
export interface MarkedClues {
  rows?: number[][];
  cols?: number[][];
}

/**
 * Mosaic's play blob. Everything past `PlayData` is optional because plays saved before the field
 * existed simply don't carry it, and must read as "nothing marked".
 */
export interface MosaicPlayData extends PlayData<CellState> {
  markedClues?: MarkedClues;
}

// ---------------------------------------------------------------------------
// Source pixel-art icon
//
// The output of the extractor (logic/extract.ts) and the raw asset a Mosaic board
// is built from — not something the client ever sees directly.
// ---------------------------------------------------------------------------

export interface PixelIcon {
  /** Icon index on the source sprite sheet (for provenance / debugging). */
  index?: number;
  width: number;
  height: number;
  /**
   * Palette of hex colours, indexed directly by `pixels`. Entry 0 is the colour the icon was drawn
   * over — what "off" cells reveal to on completion — and is never an art colour; entries 1+ are
   * the art. White is an art colour like any other unless the background itself is white.
   */
  colorMap: string[];
  /**
   * Per-pixel palette index. 0 means background: the cell isn't part of the picture, so it can
   * never be filled and never belongs to the silhouette.
   */
  pixels: number[][];
}

// ---------------------------------------------------------------------------
// Stored board data (`boards.game_data`)
//
// We store the *minimum* needed to reconstruct a puzzle: the pixel-art icon.
// Everything else on MosaicBoard (the silhouette, the clues,
// the reveal palette) is cheap to derive and is computed at load time by
// `expandGameData` in ./logic/board.ts — the same "expand the JSON blob into a
// runtime board" step the other games use.
// ---------------------------------------------------------------------------

export interface MosaicGameData extends PixelIcon {
  /**
   * Hand-authored silhouette override (admin puzzle editor), `[row][col]`, same dimensions
   * as `pixels`. When absent the silhouette is derived from `pixels` (any non-zero pixel is
   * filled), which is what extraction produces.
   *
   * It exists because extraction makes every coloured pixel part of the silhouette, which
   * often yields a boring solid blob. An editor can blank interior pixels — say the dark eyes
   * on a light face — to leave a silhouette with structure worth deducing. Blanking only
   * changes the *puzzle*: `pixels` still holds every original pixel, so the completion reveal
   * paints the full source art, including cells the player never filled.
   */
  mask?: boolean[][];
}

// ---------------------------------------------------------------------------
// Runtime board
//
// The fully-expanded board the client works with, produced from MosaicGameData by
// `expandGameData`. All the puzzle-shaped fields (`solution`, `rowClues`,
// `colClues`, `palette`, `colors`) are derived — none are persisted.
//
// `solution` and `colors` are independent: a cell can carry a reveal colour without
// being part of the silhouette (see `MosaicGameData.mask`). The reverse never holds —
// a filled cell always has a colour — because a mask only ever blanks cells.
// ---------------------------------------------------------------------------

/** Reveal palette: hex colour strings, indexed by a cell's `colors` value; entry 0 is the background. */
export type Palette = string[];

/** The derived, puzzle-shaped portion of a MosaicBoard (everything not on `Board`). */
export interface MosaicPuzzle {
  width: number;
  height: number;

  /** The target silhouette: true = filled cell. This is `Board.solution`, typed. */
  solution: boolean[][];

  /** Row clues, top-to-bottom. Derived from `solution`. */
  rowClues: Clue[];
  /** Column clues, left-to-right. Derived from `solution`. */
  colClues: Clue[];

  /** Reveal palette (hex strings); entry 0 is the background. */
  palette: Palette;
  /** Per-cell palette index for the reveal: 0 = background, N = palette[N]. */
  colors: number[][];
}

export interface MosaicBoard extends Board, MosaicPuzzle {
  solution: boolean[][];
}
